import { cn } from "@/lib/utils";
import { CheckCircle2, Clock, X } from "lucide-react";

import { SubStep } from "@/types/wizard";

interface RightSidebarProps {
  currentSubSteps?: SubStep[];
  conditionalStepSubSteps?: SubStep[];
  currentStepName?: string;
  className?: string;
}

export function RightSidebar({
  currentSubSteps,
  conditionalStepSubSteps,
  currentStepName,
  className,
}: RightSidebarProps) {
  const hasSubSteps =
    (currentSubSteps && currentSubSteps.length > 0) ||
    (conditionalStepSubSteps && conditionalStepSubSteps.length > 0);

  if (!hasSubSteps) {
    return null;
  }

  return (
    <div
      className={cn(
        "w-80 bg-card border-l border-border flex flex-col",
        className
      )}
    >
      <div className="p-6 border-b border-border">
        <h2 className="text-lg font-semibold text-foreground">
          Progres pas curent
        </h2>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* Sub-steps for current step */}
        {currentSubSteps && currentSubSteps.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <h3 className="text-base font-semibold text-primary">
                {currentStepName
                  ? `Verificări pentru ${currentStepName}`
                  : "Verificări curente"}
              </h3>
            </div>
            <div className="space-y-3">
              {currentSubSteps.map((subStep) => (
                <div
                  key={subStep.id}
                  className={cn(
                    "flex items-start gap-3 p-3 rounded-lg border transition-smooth",
                    {
                      "bg-success/5 border-success/20":
                        subStep.status === "completed",
                      "bg-primary/5 border-primary/30 shadow-sm":
                        subStep.status === "current",
                      "bg-muted/30 border-border":
                        subStep.status === "upcoming",
                      "bg-destructive/5 border-destructive/20":
                        subStep.status === "not-applicable",
                    }
                  )}
                >
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center font-semibold transition-smooth flex-shrink-0",
                      {
                        "bg-success text-white": subStep.status === "completed",
                        "bg-primary text-primary-foreground":
                          subStep.status === "current",
                        "bg-muted text-muted-foreground":
                          subStep.status === "upcoming",
                        "bg-destructive text-destructive-foreground":
                          subStep.status === "not-applicable",
                      }
                    )}
                  >
                    {subStep.status === "completed" ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : subStep.status === "current" ? (
                      <Clock className="w-4 h-4" />
                    ) : subStep.status === "not-applicable" ? (
                      <X className="w-4 h-4" />
                    ) : (
                      <span className="text-xs">
                        {subStep.id.split(".")[1]}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span
                      className={cn("text-sm font-medium block", {
                        "text-success": subStep.status === "completed",
                        "text-primary": subStep.status === "current",
                        "text-muted-foreground": subStep.status === "upcoming",
                        "text-destructive": subStep.status === "not-applicable",
                      })}
                    >
                      {subStep.label}
                    </span>
                    <div
                      className={cn("text-xs mt-1", {
                        "text-success/70": subStep.status === "completed",
                        "text-primary/70": subStep.status === "current",
                        "text-muted-foreground/70":
                          subStep.status === "upcoming",
                        "text-destructive/70":
                          subStep.status === "not-applicable",
                      })}
                    >
                      {subStep.status === "completed" && "✓ Completat"}
                      {subStep.status === "current" && "🕐 În desfășurare"}
                      {subStep.status === "upcoming" && "⏳ În așteptare"}
                      {subStep.status === "not-applicable" && "✗ Nu se aplică"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Sub-steps for conditional step */}
        {conditionalStepSubSteps && conditionalStepSubSteps.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <div className="w-2 h-2 bg-primary rounded-full"></div>
              <h3 className="text-base font-semibold text-primary">
                Analiză de Impact
              </h3>
            </div>
            <div className="space-y-3">
              {conditionalStepSubSteps.map((subStep) => (
                <div
                  key={subStep.id}
                  className={cn(
                    "flex items-start gap-3 p-3 rounded-lg border transition-smooth",
                    {
                      "bg-success/5 border-success/20":
                        subStep.status === "completed",
                      "bg-primary/5 border-primary/30 shadow-sm":
                        subStep.status === "current",
                      "bg-muted/30 border-border":
                        subStep.status === "upcoming",
                      "bg-destructive/5 border-destructive/20":
                        subStep.status === "not-applicable",
                    }
                  )}
                >
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center font-semibold transition-smooth flex-shrink-0",
                      {
                        "bg-success text-white": subStep.status === "completed",
                        "bg-primary text-primary-foreground":
                          subStep.status === "current",
                        "bg-muted text-muted-foreground":
                          subStep.status === "upcoming",
                        "bg-destructive text-destructive-foreground":
                          subStep.status === "not-applicable",
                      }
                    )}
                  >
                    {subStep.status === "completed" ? (
                      <CheckCircle2 className="w-4 h-4" />
                    ) : subStep.status === "current" ? (
                      <Clock className="w-4 h-4" />
                    ) : subStep.status === "not-applicable" ? (
                      <X className="w-4 h-4" />
                    ) : (
                      <span className="text-xs">
                        {subStep.id.split(".")[1]}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span
                      className={cn("text-sm font-medium block", {
                        "text-success": subStep.status === "completed",
                        "text-primary": subStep.status === "current",
                        "text-muted-foreground": subStep.status === "upcoming",
                        "text-destructive": subStep.status === "not-applicable",
                      })}
                    >
                      {subStep.label}
                    </span>
                    <div
                      className={cn("text-xs mt-1", {
                        "text-success/70": subStep.status === "completed",
                        "text-primary/70": subStep.status === "current",
                        "text-muted-foreground/70":
                          subStep.status === "upcoming",
                        "text-destructive/70":
                          subStep.status === "not-applicable",
                      })}
                    >
                      {subStep.status === "completed" && "✓ Completat"}
                      {subStep.status === "current" && "🕐 În desfășurare"}
                      {subStep.status === "upcoming" && "⏳ În așteptare"}
                      {subStep.status === "not-applicable" && "✗ Nu se aplică"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
